import React, { useState, useRef, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Upload, File, X, AlertCircle, FolderPlus, ArrowRight, Sparkles } from 'lucide-react';
import { Button, Select, Label, ErrorMessage } from '../styles/GlobalStyles';
import { theme } from '../styles/theme';
import { FormData as TenderFormData, ResponseMode } from '../types';
import SuccessfulTenderUploader from './SuccessfulTenderUploader';
import ResponseModeSelector from './ResponseModeSelector';
import { useAuth } from '../context/AuthContext';
import { ProjectApi, ProjectData } from '../services/projectApi';
import { TenderMode } from './SuccessfulTenderModeSelector';
import { TenderDocumentData } from '../services/tenderDocumentApi';
import { uploadService } from '../services/uploadService';

const FormContainer = styled.div`
  max-width: 100%;
`;

const FormField = styled.div`
  margin-bottom: ${theme.spacing[6]};
`;

const DropZone = styled.div<{ $isDragOver: boolean; $hasFile: boolean }>`
  border: 2px dashed ${({ $isDragOver, $hasFile }) => 
    $isDragOver 
      ? theme.colors.primary[500] 
      : $hasFile 
        ? theme.colors.success[500] 
        : theme.colors.border
  };
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[8]};
  text-align: center;
  background: ${({ $isDragOver, $hasFile }) => 
    $isDragOver 
      ? theme.colors.primary[100] 
      : $hasFile 
        ? theme.colors.success[100] 
        : theme.colors.backgroundSecondary
  };
  transition: all ${theme.transitions.base};
  cursor: pointer;
  position: relative;

  &:hover {
    border-color: ${theme.colors.primary[400]};
    background: ${theme.colors.primary[100]};
  }
`;

const DropZoneContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing[4]};
`;

const DropZoneIcon = styled.div<{ $hasFile: boolean }>`
  width: 64px;
  height: 64px;
  border-radius: ${theme.borderRadius.full};
  background: ${({ $hasFile }) => $hasFile ? theme.colors.success[100] : theme.colors.primary[100]};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${theme.spacing[2]};

  svg {
    width: 24px;
    height: 24px;
    color: ${({ $hasFile }) => $hasFile ? theme.colors.success[600] : theme.colors.primary[600]};
  }
`;

const DropZoneText = styled.div`
  h3 {
    color: ${theme.colors.textPrimary};
    font-size: ${theme.typography.fontSize.lg};
    font-weight: ${theme.typography.fontWeight.semibold};
    margin-bottom: ${theme.spacing[2]};
  }

  p {
    color: ${theme.colors.textSecondary};
    font-size: ${theme.typography.fontSize.sm};
    line-height: ${theme.typography.lineHeight.relaxed};
  }
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  margin-top: ${theme.spacing[4]};
`;

const FileDetails = styled.div`
  flex: 1;
  
  .filename {
    font-weight: ${theme.typography.fontWeight.medium};
    color: ${theme.colors.textPrimary};
    font-size: ${theme.typography.fontSize.sm};
  }
  
  .filesize {
    color: ${theme.colors.textSecondary};
    font-size: ${theme.typography.fontSize.xs};
    margin-top: ${theme.spacing[1]};
  }
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: ${theme.colors.textSecondary};
  cursor: pointer;
  border-radius: ${theme.borderRadius.md};
  transition: all ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.error[100]};
    color: ${theme.colors.error[600]};
  }
`;

const HelpText = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  margin-top: ${theme.spacing[1]};
`;

const InfoBox = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.info[100]};
  border: 1px solid ${theme.colors.info[500]}20;
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing[6]};
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoTitle = styled.h4`
  color: ${theme.colors.info[700]};
  margin-bottom: ${theme.spacing[1]};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.sm};
`;

const InfoText = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const HiddenInput = styled.input`
  display: none;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
  margin-top: ${theme.spacing[2]};
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background: ${theme.colors.primary[500]};
  border-radius: ${theme.borderRadius.full};
  transition: width 0.3s ease;
  width: ${({ progress }) => progress}%;
`;

const NoProjectsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${theme.spacing[8]} ${theme.spacing[6]};
  text-align: center;
  background: linear-gradient(135deg, ${theme.colors.primary[50]}, ${theme.colors.accent[50]});
  border: 2px dashed ${theme.colors.primary[200]};
  border-radius: ${theme.borderRadius.xl};
  margin: ${theme.spacing[2]} 0;
  position: relative;
  overflow: hidden;
  transition: all ${theme.transitions.base};
  
  &:hover {
    border-color: ${theme.colors.primary[300]};
    background: linear-gradient(135deg, ${theme.colors.primary[100]}, ${theme.colors.accent[100]});
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.lg};
  }
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle at center,
      ${theme.colors.primary[100]}20 0%,
      transparent 50%
    );
    animation: float 6s ease-in-out infinite;
    pointer-events: none;
  }
  
  @keyframes float {
    0%, 100% { transform: translate(0, 0) rotate(0deg); }
    33% { transform: translate(10px, -10px) rotate(1deg); }
    66% { transform: translate(-5px, 5px) rotate(-1deg); }
  }
`;

const NoProjectsIcon = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: ${theme.borderRadius.full};
  background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[600]});
  color: white;
  margin-bottom: ${theme.spacing[4]};
  box-shadow: ${theme.shadows.lg};
  
  &::after {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    border-radius: ${theme.borderRadius.full};
    background: linear-gradient(45deg, ${theme.colors.primary[300]}, ${theme.colors.accent[300]});
    z-index: -1;
    animation: pulse-border 2s ease-in-out infinite;
  }
  
  @keyframes pulse-border {
    0%, 100% { opacity: 0.7; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.05); }
  }
`;

const NoProjectsTitle = styled.h3`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  margin-bottom: ${theme.spacing[3]};
  font-family: ${theme.typography.fontFamily.display};
  position: relative;
  z-index: 1;
`;

const NoProjectsDescription = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.textSecondary};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin-bottom: ${theme.spacing[6]};
  max-width: 400px;
  position: relative;
  z-index: 1;
`;

const CreateProjectButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[600]});
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  box-shadow: ${theme.shadows.md};
  position: relative;
  z-index: 1;
  
  &:hover {
    background: linear-gradient(135deg, ${theme.colors.primary[600]}, ${theme.colors.accent[700]});
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.xl};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    transition: transform ${theme.transitions.base};
  }
  
  &:hover svg:last-child {
    transform: translateX(2px);
  }
`;

interface FileUploadTabProps {
  onSubmit: (data: TenderFormData) => void;
  isLoading?: boolean;
}

const FileUploadTab: React.FC<FileUploadTabProps> = ({ 
  onSubmit, 
  isLoading = false 
}) => {
  const { state: authState } = useAuth();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formData, setFormData] = useState({
    project_id: '',
  });
  const [successfulTenderMode, setSuccessfulTenderMode] = useState<TenderMode>('select');
  const [responseMode, setResponseMode] = useState<ResponseMode>('generate');
  const [selectedBankDocuments, setSelectedBankDocuments] = useState<TenderDocumentData[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [projects, setProjects] = useState<ProjectData[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch projects on component mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setIsLoadingProjects(true);
        const response = await ProjectApi.getProjects(1, 100); // Get first 100 projects
        
        if (response.success && response.data) {
          setProjects(response.data.projects);
        } else {
          console.error('Failed to fetch projects:', response.error);
          setErrors(prev => ({ ...prev, projects: response.error || 'Failed to load projects' }));
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
        setErrors(prev => ({ ...prev, projects: 'Failed to load projects' }));
      } finally {
        setIsLoadingProjects(false);
      }
    };

    fetchProjects();
  }, []);

  const validateFile = (file: File): boolean => {
    const validation = uploadService.validateFile(file);
    
    if (!validation.isValid) {
      setErrors(prev => ({ ...prev, file: validation.error || 'Invalid file' }));
      return false;
    }
    
    setErrors(prev => ({ ...prev, file: '' }));
    return true;
  };

  const handleFileSelect = useCallback((file: File) => {
    if (validateFile(file)) {
      setSelectedFile(file);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const uploadFile = async (file: File): Promise<string> => {
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const url = await uploadService.uploadFile(file, {
        onProgress: (progress) => setUploadProgress(progress),
        timeout: 30000
      });
      
      return url;
    } catch (error) {
      console.error('Upload error:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to upload file');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!selectedFile) {
      newErrors.file = 'Please select a file to upload';
    }

    if (!formData.project_id.trim()) {
      newErrors.project_id = 'Please select a project';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const processSubmission = async () => {
    if (!selectedFile) return;

    try {
      // Upload file using unified service
      const fileUrl = await uploadFile(selectedFile);

      // Get successful tender URLs from selected bank documents
      const successfulUrls: string[] = [];

      if (successfulTenderMode === 'select' && selectedBankDocuments.length > 0) {
        // Use URLs from selected tender bank documents
        successfulUrls.push(...selectedBankDocuments.map(doc => doc.url).filter((url): url is string => Boolean(url)));
      }
      // If mode is 'none', successfulUrls remains empty

      // Get company_id from user or use default demo value
      const company_id = authState.user?.id || '318';

      const submitData: TenderFormData = {
        url: fileUrl, // Use uploaded file URL for analysis
        company_id: company_id,
        project_id: formData.project_id,
        successful_tender_urls: successfulUrls,
        response_mode: responseMode,
      };

      onSubmit(submitData);
    } catch (error) {
      console.error('Submit error:', error);
      setErrors(prev => ({
        ...prev,
        file: error instanceof Error ? error.message : 'Upload failed. Please try again.'
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm() && selectedFile) {
      processSubmission();
    }
  };

  return (
    <FormContainer>
      <InfoBox>
        <AlertCircle size={20} color={theme.colors.info[500]} />
        <InfoContent>
          <InfoTitle>File Upload</InfoTitle>
          <InfoText>
            Upload your tender document directly from your computer. Supported formats: PDF, DOC, DOCX (max 10MB).
          </InfoText>
        </InfoContent>
      </InfoBox>

      <form onSubmit={handleSubmit}>
        <FormField>
          <Label>
            Tender Document *
          </Label>
          <DropZone
            $isDragOver={isDragOver}
            $hasFile={!!selectedFile}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={handleClick}
          >
            <DropZoneContent>
              <DropZoneIcon $hasFile={!!selectedFile}>
                {selectedFile ? <File /> : <Upload />}
              </DropZoneIcon>
              <DropZoneText>
                {selectedFile ? (
                  <>
                    <h3>File Ready</h3>
                    <p>File selected successfully. Click submit to analyze.</p>
                  </>
                ) : (
                  <>
                    <h3>Drop files here or click to browse</h3>
                    <p>Upload your tender document (PDF, DOC, DOCX up to 10MB)</p>
                  </>
                )}
              </DropZoneText>
            </DropZoneContent>
          </DropZone>
          
          <HiddenInput
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={handleFileInputChange}
          />
          
          {selectedFile && (
            <FileInfo>
              <File size={20} color={theme.colors.success[600]} />
              <FileDetails>
                <div className="filename">{selectedFile.name}</div>
                <div className="filesize">{formatFileSize(selectedFile.size)}</div>
                {isUploading && (
                  <ProgressBar>
                    <ProgressFill progress={uploadProgress} />
                  </ProgressBar>
                )}
              </FileDetails>
              <RemoveButton onClick={removeFile} type="button" disabled={isUploading}>
                <X size={16} />
              </RemoveButton>
            </FileInfo>
          )}
          
          {errors.file && <ErrorMessage>{errors.file}</ErrorMessage>}
          <HelpText>
            Drag and drop your file here or click to select from your computer
          </HelpText>
        </FormField>


        <FormField>
          <Label>
            Select Project *
          </Label>
          <Select
            value={formData.project_id}
            onChange={(e) => setFormData(prev => ({ ...prev, project_id: e.target.value }))}
            disabled={isLoading || isLoadingProjects}
            $hasError={!!errors.project_id}
          >
            <option value="">
              {isLoadingProjects ? '-- Loading projects... --' : '-- Please select a project --'}
            </option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </Select>
          {errors.project_id && <ErrorMessage>{errors.project_id}</ErrorMessage>}
          {errors.projects && <ErrorMessage>{errors.projects}</ErrorMessage>}
          {projects.length === 0 && !isLoadingProjects ? (
            <NoProjectsContainer>
              <NoProjectsIcon>
                <FolderPlus size={32} />
              </NoProjectsIcon>
              <NoProjectsTitle>No Projects Found</NoProjectsTitle>
              <NoProjectsDescription>
                Start your tender analysis journey by creating your first project. 
                Projects help organize and manage your tender documents effectively.
              </NoProjectsDescription>
              <Link to="/projects" style={{ textDecoration: 'none' }}>
                <CreateProjectButton>
                  <Sparkles size={16} />
                  Create Your First Project
                  <ArrowRight size={16} />
                </CreateProjectButton>
              </Link>
            </NoProjectsContainer>
          ) : (
            <HelpText>
              Associate this analysis with an existing project
            </HelpText>
          )}
        </FormField>

        <FormField>
          <Label>
            Response Mode
          </Label>
          <ResponseModeSelector
            mode={responseMode}
            onChange={setResponseMode}
            disabled={isLoading || isUploading}
          />
          <HelpText>
            Choose how the AI should process your tender document
          </HelpText>
        </FormField>

        <FormField>
          <Label>
            Tender Bank
          </Label>
          <SuccessfulTenderUploader
            disabled={isLoading || isUploading}
            mode={successfulTenderMode}
            onModeChange={setSuccessfulTenderMode}
            selectedBankDocuments={selectedBankDocuments}
            onBankDocumentsChange={setSelectedBankDocuments}
            showModeSelector={false}
          />
        </FormField>

        <Button
          type="submit"
          $size="lg"
          $fullWidth
          disabled={isLoading || !selectedFile || isUploading}
        >
          {isUploading ? (
            <>
              <div className="pulse">Uploading... {Math.round(uploadProgress)}%</div>
            </>
          ) : isLoading ? (
            <>
              <div className="pulse">Processing...</div>
            </>
          ) : (
            <>
              <Upload size={20} />
              Start Analysis
            </>
          )}
        </Button>
      </form>
    </FormContainer>
  );
};

export default FileUploadTab;